import { useEffect } from "react";

import { Store } from "@/core/data_sources/store";
import type { BasicQuery } from "@/core/data_sources/services";
import { useService } from "@/core/utils/hooks";

import {
  getPermissionProfilePresetByQuery,
  getPermissionProfilePresetUsageByQuery,
  listPermissionProfilePresets,
  type PermissionProfilePresetsQuery,
} from "../services";
import { PermissionProfilePresetsState } from "./state";

import type { PermissionProfilePresetsActions } from "./actions";
import type { PermissionProfilePresetsHooks } from "./hooks";

export class PermissionProfilePresetsStore extends Store<
  PermissionProfilePresetsState,
  PermissionProfilePresetsActions,
  PermissionProfilePresetsHooks
> {
  private static _instance: PermissionProfilePresetsStore;

  static get instance() {
    PermissionProfilePresetsStore._instance =
      PermissionProfilePresetsStore._instance ||
      new PermissionProfilePresetsStore();
    return this._instance;
  }

  constructor() {
    super(
      new PermissionProfilePresetsState(),
      {
        setPresets: (list) => {
          this.state.setPresets(list);
        },
        setSelected: (preset) => {
          this.state.setSelected(preset);
        },
      },
      {
        usePermissionProfilePresetsStoreService: <
          Q extends PermissionProfilePresetsQuery,
        >(
          q: Q,
        ) => {
          const { data, error, loading, query, setQuery } = useService(
            listPermissionProfilePresets,
            q,
          );
          useEffect(() => {
            if (error) return;
            if (!loading && data) {
              this.state.setPresets(data.data ?? []);
            }
          }, [loading, data, error]);
          return { data, error, loading, query, setQuery };
        },
        usePresetDetailService: <
          Q extends BasicQuery & { id: string | number },
        >(
          q: Q,
        ) => {
          const { data, error, loading, query, setQuery } = useService(
            getPermissionProfilePresetByQuery,
            q,
          );
          useEffect(() => {
            if (error) return;
            if (!loading && data) {
              this.state.setSelected(data.data);
            }
          }, [loading, data, error]);
          return { data, error, loading, query, setQuery };
        },
        usePresetUsageService: <Q extends BasicQuery & { id: string | number }>(
          q: Q,
        ) => {
          const { data, error, loading, query, setQuery } = useService(
            getPermissionProfilePresetUsageByQuery,
            q,
          );
          useEffect(() => {
            if (error) return;
            if (!loading && data) {
              this.state.setUsage(data.data);
            }
          }, [loading, data, error]);
          return { data, error, loading, query, setQuery };
        },
      },
    );
  }
}
